class DragNDrop {

    /**
     * Constructor for the DragNDrop class.
     *
     * @param {string} contents - Selector for draggable elements.
     * @param {string} draggers - Selector for elements triggering the drag.
     * @param {string} movingCss - CSS string for the moving icon and style.
     */
    constructor(contents, draggers, movingCss = `
            .moving{
                padding-left:2em;
            }
            .moving::before{
                margin-left:-1.1em;
                position:absolute;
                font-size:1.5em;
                font-family:"Font Awesome 7 Free";
                font-weight:400;content:"\\f35a";
                transform:rotate(-40deg);
            }`) {
        this.contents = contents;
        this.draggers = draggers;
        this.dragSource = null;
        this.dropCallback = null;
        this.initialized = new WeakSet();
        // A reorder is asynchronous. Keep each sibling list stable until its
        // revision-aware save finishes (or has rolled back on failure).
        this.pending = new WeakSet();
        this.movingCss = movingCss;

        this.handleDragStart = this.handleDragStart.bind(this);
        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleDrop = this.handleDrop.bind(this);
        this.handleDragEnd = this.handleDragEnd.bind(this);

        this.addStyles();
    }

    /** Adds CSS styles for the drag-and-drop effect. */
    addStyles() {
        const style = document.createElement('style');
        style.type = 'text/css';
        style.innerHTML = this.movingCss;
        document.head.appendChild(style);
    }

    /** Start only when dragging one of this row's designated handles. */
    handleDragStart(event) {
        const source = event.target.closest?.(this.contents);
        const handle = event.target.closest?.(this.draggers);
        if (!source || !handle || handle.closest(this.contents) !== source || !event.dataTransfer) return;
        event.stopPropagation();
        this.dragSource = source;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', 'node'); // Required by Firefox
    }

    /** The listener's row matters: a section can contain nested draggable rows. */
    targetRow(event) {
        return event.currentTarget?.matches?.(this.contents)
            ? event.currentTarget
            : event.target.closest?.(this.contents);
    }

    handleDragOver(event) {
        if (!this.dragSource) return;
        const target = this.targetRow(event);
        // Let the drag event bubble to a parent row when the pointer is over
        // nested Contents/Links but the user is dragging an outer Section.
        if (!target || target.parentNode !== this.dragSource.parentNode) return;
        event.preventDefault();
        event.stopPropagation();
        target.querySelector?.(this.draggers)?.classList.add('moving');
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    }

    handleDragLeave(event) {
        this.targetRow(event)?.querySelector?.(this.draggers)?.classList.remove('moving');
    }

    handleDragEnd(event) {
        this.targetRow(event)?.querySelector?.(this.draggers)?.classList.remove('moving');
        this.dragSource = null;
    }

    /**
     * Insert above a target when moving upwards and BELOW it when moving
     * downwards. Always inserting before the target makes an adjacent downward
     * drag a no-op and prevents placing a row at the end of the list.
     */
    handleDrop(event) {
        const draggedContent = this.dragSource;
        const targetContent = this.targetRow(event);
        if (!draggedContent || !targetContent) return;

        const parent = targetContent.parentNode;
        if (!parent || draggedContent.parentNode !== parent) {
            // Do not swallow events from nested sortable lists. An outer
            // parent row may be the intended sibling drop target.
            return;
        }
        event.stopPropagation();
        event.preventDefault();
        targetContent.querySelector?.(this.draggers)?.classList.remove('moving');
        if (targetContent === draggedContent || this.pending.has(parent)) return;

        const previousOrder = [...parent.children];
        const sourceIndex = previousOrder.indexOf(draggedContent);
        const targetIndex = previousOrder.indexOf(targetContent);
        if (sourceIndex < 0 || targetIndex < 0) return;

        // insertBefore(node, null) appends to the end.
        const reference = sourceIndex < targetIndex ? targetContent.nextSibling : targetContent;
        parent.insertBefore(draggedContent, reference);

        if (this.dropCallback) {
            this.pending.add(parent);
            Promise.resolve()
                .then(() => this.dropCallback(parent))
                .catch(() => {
                    // Keep the exact same nodes (and Tom Select/Quill instances).
                    // The Article callback already displays the server error.
                    previousOrder.forEach((element) => parent.appendChild(element));
                })
                .finally(() => this.pending.delete(parent));
        }
    }

    /**
     * Initializes DragNDrop on rows, including newly inserted AJAX fragments.
     * Existing rows are wired only once, even if moved within the DOM.
     */
    init(dropCallback, root = document) {
        this.dropCallback = dropCallback;
        const elements = [
            ...(root.matches?.(this.contents) ? [root] : []),
            ...root.querySelectorAll(this.contents),
        ];

        elements.forEach((element) => {
            if (this.initialized.has(element)) return;
            this.initialized.add(element);
            const triggers = element.querySelectorAll(this.draggers);

            triggers.forEach((trigger) => {
                if (trigger.closest(this.contents) !== element) return;
                trigger.setAttribute('draggable', true);
            });

            element.addEventListener('dragstart', this.handleDragStart, false);
            element.addEventListener('dragover', this.handleDragOver, false);
            element.addEventListener('dragleave', this.handleDragLeave, false);
            element.addEventListener('drop', this.handleDrop, false);
            element.addEventListener('dragend', this.handleDragEnd, false);
        });
    }
}

export default DragNDrop;
